import { message, notification } from 'antd';
import { capitalize, forIn } from 'lodash';
import moment from 'moment';
import client from '../apollo';
import { GET_SIGNED_URL } from '../components/graphql/Mutation';
import api from './api';
import {
  ALLOW_IMAGES_TYPES,
  ALLOW_IMAGE_TYPES,
  ALLOW_VIDEO_TYPES,
  CRITICAL_ORDERS_KEY,
  MAX_BANNER_IMAGE_SIZE,
  MAX_BRANDS_IMAGE_SIZE,
  MAX_GENRE_IMAGE_SIZE,
  MAX_IMAGE_SIZE,
  MAX_STORY_VIDEO_SIZE,
  MAX_VIDEO_SIZE,
  ORDER_STATUS,
  PLAN_NAMES,
  REGEX,
  defaultDateFormat,
} from './constants';

// Portal related methods
export const injectUsingPortal = (portalId) =>
  // eslint-disable-next-line no-undef
  document?.getElementById(portalId);

export const isPortalIdExists = (portalId) => !!injectUsingPortal(portalId);

// Check for document Id's exists
export const getElementFromDocumentId = (portalId) =>
  // eslint-disable-next-line no-undef
  document?.getElementById(portalId);

export const isDocumentIdExist = (portalId) =>
  !!getElementFromDocumentId(portalId);
// Check for document Id's exists end

export const formatDate = (
  dateTime,
  format = `${defaultDateFormat} hh:mm A`,
) => {
  if (dateTime && moment && format) {
    return moment(dateTime)?.format(format);
  }

  return dateTime || '-';
};

export const formValidatorRules = {
  required: {
    required: true,
    message: 'Required',
    whitespace: true,
  },
  whitespaceNotAllowed: {
    required: false,
    message: 'Should be include characters!',
    whitespace: true,
  },
  email: () => ({
    validator(rule, value) {
      if (!value?.trim()) {
        return Promise?.resolve();
      }
      if (!REGEX?.EMAIL?.test(value)) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject('Should be a valid E-mail!');
      }
      return Promise?.resolve();
    },
  }),
  name: () => ({
    validator(rule, value) {
      if (!value) {
        return Promise?.resolve();
      }
      if (!REGEX?.NAME?.test(value)) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject('Should be a valid Name!');
      }
      return Promise?.resolve();
    },
  }),
  number: () => ({
    validator(rule, value) {
      if (!value) {
        return Promise?.resolve();
      }
      if (!Number(value) || !REGEX?.NUMBER?.test(Number(value))) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject('Should be a valid Number!');
      }
      return Promise?.resolve();
    },
  }),
  phone: () => ({
    validator(rule, value) {
      if (!value?.trim()) {
        return Promise?.resolve();
      }
      if (!Number(value) || !REGEX?.PHONE?.test(Number(value))) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject('Should be a valid Contact Number!');
      }
      return Promise?.resolve();
    },
  }),
  positiveNumber: (isZeroAllowed = true) => ({
    validator(rule, value) {
      if (!value) {
        return Promise.resolve();
      }

      const valueString = value.toString();
      const isValueValid = REGEX?.POSITIVE_NUMBERS?.test(valueString);

      if (valueString.length > 20) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject('Maximum 20 digits allowed!');
      }

      if (isZeroAllowed) {
        if (!isValueValid) {
          // eslint-disable-next-line prefer-promise-reject-errors
          return Promise.reject('Should be a valid Number!');
        }
      } else if (!isValueValid || parseInt(value, 10) === 0) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject('Should be a valid Number!');
      }

      return Promise.resolve();
    },
  }),
  price: () => ({
    validator(rule, value) {
      if (!value) {
        return Promise?.resolve();
      }
      if (!REGEX?.PRICE?.test(value)) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject('Should be a valid Price!');
      }
      const numericValue = parseFloat(
        value?.toString()?.replace(REGEX?.NOT_DIGIT_CHARACTER, ''),
      );
      const maxValue = 10000000;
      if (numericValue > maxValue) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject('Should not exceed 10000000!');
      }
      return Promise?.resolve();
    },
  }),
  minAgeRule: (minAge, maxAge) => ({
    validator(rule, value) {
      if (!value || !maxAge) {
        return Promise?.resolve();
      }
      const minAgeInt = parseInt(minAge, 10);
      const maxAgeInt = parseInt(maxAge, 10);
      const maxValue = 1200;
      if (maxAgeInt < minAgeInt) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject('Not exceed than max age');
      }
      if (minAgeInt > maxValue) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject('Should not exceed 1200 months!');
      }
      return Promise?.resolve();
    },
  }),
  maxAgeRule: (minAge, maxAge) => ({
    validator(rule, value) {
      if (!value || !minAge) {
        return Promise?.resolve();
      }
      const minAgeInt = parseInt(minAge, 10);
      const maxAgeInt = parseInt(maxAge, 10);
      const maxValue = 1200;
      if (minAgeInt > maxAgeInt) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject('Not lower than min age');
      }
      if (maxAgeInt > maxValue) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject('Should not exceed 1200 months!');
      }
      return Promise?.resolve();
    },
  }),
  latRule: (lng) => ({
    validator(rule, value) {
      if (lng && !value) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject('Please Enter Latitude!');
      }
      return Promise?.resolve();
    },
  }),
  lngRule: (lat) => ({
    validator(rule, value) {
      if (lat && !value) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject('Please Enter Longitude!');
      }
      return Promise?.resolve();
    },
  }),

  quickDeliveryAreaRule: (isEnabled) => ({
    validator(rule, value) {
      if (isEnabled && !value) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject('Please Enter Serviceable Area!');
      }
      return Promise.resolve();
    },
  }),

  password: () => ({
    validator(rule, value) {
      if (!value) {
        return Promise?.resolve();
      }

      if (!REGEX?.PASSWORD?.test(value) && value?.length < 8) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject(
          `Please enter a password with a minimum of 8 characters!`,
        );
      }

      if (!REGEX?.PASSWORD?.test(value) && value?.length > 16) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject(
          `Please ensure that your password is no longer than 16 characters!`,
        );
      }

      if (!REGEX?.PASSWORD?.test(value)) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject(`Password is weak!`);
      }
      return Promise?.resolve();
    },
  }),
  url: () => ({
    validator(rule, value) {
      if (!value) {
        return Promise?.resolve();
      }
      if (!REGEX?.WEB_URL?.test(value)) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject(`Should be a valid URL!`);
      }
      return Promise?.resolve();
    },
  }),
  pincodeRule: () => ({
    validator(rule, value) {
      if (!value) {
        return Promise?.resolve();
      }
      if (!REGEX?.PINCODE?.test(value)) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject(`Should be a valid pincode!`);
      }
      return Promise?.resolve();
    },
  }),
  notNumString: () => ({
    validator(rule, value) {
      if (!value) {
        return Promise?.resolve();
      }
      if (!REGEX?.NOT_NUM_STRING?.test(value)) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject(`Should be a valid string!`);
      }
      return Promise?.resolve();
    },
  }),
  configValue: () => ({
    validator(rule, value) {
      if (!value) {
        return Promise?.resolve();
      }
      if (parseInt(value, 10) <= 0) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject(`Value should be greater than 0.`);
      }
      if (parseInt(value, 10) > 1000) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject(`Should not exceed 1000!`);
      }
      return Promise?.resolve();
    },
  }),
  couponCode: () => ({
    validator(rule, value) {
      if (!value) {
        return Promise?.resolve();
      }
      if (!REGEX?.COUPON?.test(value)) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject('Should be a valid Code!');
      }
      return Promise?.resolve();
    },
  }),
  couponName: () => ({
    validator(rule, value) {
      if (!value) {
        return Promise?.resolve();
      }
      if (!REGEX?.COUPON?.test(value)) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject('Should be a valid Name!');
      }
      return Promise?.resolve();
    },
  }),
  videoTitle: () => ({
    validator(rule, value) {
      if (!value) {
        return Promise?.resolve();
      }
      if (!REGEX?.NAME?.test(value)) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject('Should be a valid Title!');
      }
      return Promise?.resolve();
    },
  }),
  percentage: () => ({
    validator(rule, value) {
      if (!value) {
        return Promise?.resolve();
      }
      const parsedValue = parseInt(value, 10);
      if (parsedValue <= 0 || parsedValue > 100) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise?.reject(`Percentage must be between 1 and 100!`);
      }
      return Promise?.resolve();
    },
  }),
};

export const combineDateTimeAndGetISOString = (date, time) => {
  const timeObj = new Date(time);
  const dateObj = new Date(date);

  let formattedDateTime = dateObj?.setUTCHours(timeObj?.getUTCHours());
  formattedDateTime = new Date(formattedDateTime)?.setUTCMinutes(
    timeObj?.getUTCMinutes(),
  );
  formattedDateTime = new Date(formattedDateTime)?.toISOString();

  return formattedDateTime;
};

export const formatPhoneNumber = (str) => {
  // Filter only numbers from the input
  const cleaned = `${str}`?.replace(/\D/g, '');

  // Check if the input is of correct length
  const match = cleaned?.match(/^(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }

  return null;
};

export const formatPhoneNumberWithoutMask = (str) => {
  // Filter only numbers from the input
  const cleaned = `${str}`?.replace(/\D/g, '');
  if (cleaned) return cleaned;
  return null;
};

export const formatPrice = (price) => {
  const formatedPrice = price || 0;

  return Number(formatedPrice)?.toLocaleString('en', {
    style: 'currency',
    currency: 'USD',
  });
};

export const formItemProps = { normalize: (value) => value?.trim() };

export const openNotification = (type, msg) => {
  notification[type]({
    message: msg,
    placement: 'topRight',
    duration: 5,
  });
};

// Note : Function to upload on s3 bucket
export async function fileUpload(signedUrl, image, onUploadProgress) {
  return new Promise((resolve, reject) => {
    try {
      // eslint-disable-next-line no-undef
      const xhr = new XMLHttpRequest();
      xhr?.open('PUT', signedUrl);
      xhr?.setRequestHeader('Content-Type', image?.type);
      xhr?.addEventListener('error', (error) => {
        if (error) {
          reject(error);
        }
      });
      xhr?.addEventListener('load', function () {
        if (this.readyState === 4) {
          if (xhr?.status === 200) {
            resolve(xhr?.response);
          } else {
            reject(xhr?.status);
          }
        }
      });

      if (onUploadProgress) {
        xhr.upload.onprogress = (e) => {
          let percentComplete = 0;
          percentComplete = Math?.ceil((e?.loaded / e?.total) * 100);
          onUploadProgress(percentComplete);
        };
      }
      xhr?.send(image);
    } catch (error) {
      reject(error);
      message?.error(error?.message);
    }
  });
}

export const getSignedUrl = async (fileObj) => {
  const fileName = fileObj?.name;

  const extension = fileName?.slice(fileName?.lastIndexOf('.') + 1);
  const key = `${fileName}`;

  const response = await client?.mutate({
    mutation: GET_SIGNED_URL,
    variables: {
      action: 'write',
      data: {
        extension: `.${extension}`,
        contentType: fileObj?.type,
        key,
      },
    },
  });
  if (response) {
    return response?.data;
  }
  return null;
};

export const uploadImage = async (signedRequest, fileObj) => {
  await api(signedRequest, {
    method: 'PUT',
    data: fileObj?.originFileObj || fileObj,
    headers: {
      'Content-Type': fileObj?.type,
    },
  });
};

export const fetchImage = async (fileObj) => {
  const fileName = fileObj?.name;
  const extension = fileName?.slice(fileName?.lastIndexOf('.') + 1);
  const key = `${fileName}`;

  const response = await client?.mutate({
    mutation: GET_SIGNED_URL,
    variables: {
      action: 'read',
      data: {
        extension: `.${extension}`,
        contentType: fileObj?.type,
        key,
      },
    },
  });
  if (response) {
    return response?.data;
  }
  return null;
};

export const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    // eslint-disable-next-line no-undef
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader?.result);
    reader.onerror = (error) => reject(error);
  });

export const getTimeFromMins = (mins) => {
  const hours = Math.floor(mins / 60);
  const minutes = mins % 60;
  return `${hours}h ${minutes}m`;
};

export const getBase64File = (img, callback) => {
  // eslint-disable-next-line no-undef
  const reader = new FileReader();
  reader?.addEventListener('load', () => callback(reader?.result));
  reader?.readAsDataURL(img);
};

export const beforeUpload = (file) => {
  const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
  if (!isJpgOrPng) {
    message?.error('You can only upload JPG/PNG file!');
  }
  const isLt2M = file?.size / 1024 / 1024 < 2;
  if (!isLt2M) {
    message?.error('Image must smaller than 2MB!');
  }
  return isJpgOrPng && isLt2M;
};

export const findPermission = (permissionObj) => {
  let permission;
  forIn(permissionObj, (value, key) => {
    if (permissionObj?.[key] === true) {
      permission = key;
    }
  });
  return permission;
};

export const verifyPermission = (permissions, routeKey, allowedPermission) => {
  const modulePermission = permissions?.find(
    (item) => item?.routeKey === routeKey,
  );
  const permission = findPermission(modulePermission);
  const hasPermission = allowedPermission?.includes(permission);
  return hasPermission;
};

export const verifyTabsPermission = (
  permissions,
  routeKey,
  tabKey,
  allowedPermission,
) => {
  const modulePermission = permissions?.find(
    (item) => item?.routeKey === routeKey,
  );
  const permission = findPermission(
    modulePermission?.tabKeys?.find((item) => item?.key === tabKey),
  );
  const hasPermission = allowedPermission?.includes(permission);
  return hasPermission;
};

export const getImageDimensions = (file) =>
  new Promise((resolve, reject) => {
    // eslint-disable-next-line no-undef
    const reader = new FileReader();

    reader.onload = (e) => {
      // eslint-disable-next-line no-undef
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img?.width,
          height: img?.height,
        });
      };

      img.onerror = (error) => {
        reject(error);
      };
      img.src = e?.target?.result;
    };

    reader.readAsDataURL(file);
  });
export const validateImagesGenres = async (
  info,
  form,
  setImages,
  images,
  onlyFormatValidate = false,
) => {
  if (!info?.file?.url && !info?.file?.originFileObj) {
    const {
      file: { name: fileName = '', key = '', size },
    } = info;
    const ext = fileName?.substring(fileName?.lastIndexOf('.') + 1);
    const extKey = key?.substring(key?.lastIndexOf('.') + 1);

    if (
      !ALLOW_IMAGES_TYPES?.includes(ext?.toLowerCase() || extKey?.toLowerCase())
    ) {
      setImages(info?.fileList?.slice(0, -1));
      form?.setFieldsValue({ [images]: info?.fileList?.slice(0, -1) });
      message?.destroy();
      message?.error(`${info?.file?.name} file is not image file.`);
      return false;
    }

    if (!onlyFormatValidate) {
      const dimension = await getImageDimensions(info?.file);
      const { width, height } = dimension || {};
      // const aspectRatio = width / height;

      if (width <= 150 || height <= 150) {
        setImages(info.fileList.slice(0, -1));
        form?.setFieldsValue({ [images]: info?.fileList?.slice(0, -1) });
        message?.destroy();
        message?.error(
          'The image is too small, the minimum dimensions are 150x150 pixels',
        );
        return false;
      }

      if (size > MAX_GENRE_IMAGE_SIZE) {
        setImages(info.fileList.slice(0, -1));
        form?.setFieldsValue({ [images]: info?.fileList?.slice(0, -1) });
        message?.destroy();
        message?.error('Allowed maximum size is 200kb');
        return false;
      }
    }
  }
  form?.setFieldsValue({ [images]: info?.fileList });
  setImages(info?.fileList);
  return true;
};
export const validateImagesBanner = async (
  info,
  form,
  setImages,
  images,
  onlyFormatValidate = false,
) => {
  if (!info?.file?.url && !info?.file?.originFileObj) {
    const {
      file: { name: fileName = '', key = '', size },
    } = info;
    const ext = fileName?.substring(fileName?.lastIndexOf('.') + 1);
    const extKey = key?.substring(key?.lastIndexOf('.') + 1);

    if (
      !ALLOW_IMAGES_TYPES?.includes(ext?.toLowerCase() || extKey?.toLowerCase())
    ) {
      setImages(info?.fileList?.slice(0, -1));
      form?.setFieldsValue({ [images]: info?.fileList?.slice(0, -1) });
      message?.destroy();
      message?.error(`${info?.file?.name} file is not image file.`);
      return false;
    }

    if (!onlyFormatValidate) {
      const dimension = await getImageDimensions(info?.file);
      const { width, height } = dimension || {};
      // const aspectRatio = width / height;
      if (width <= 1499 || height <= 1700) {
        setImages(info.fileList.slice(0, -1));
        form?.setFieldsValue({ [images]: info?.fileList?.slice(0, -1) });
        message?.destroy();
        message?.error(
          'The image is too small, the minimum dimensions are 1500x1720 pixels',
        );
        return false;
      }

      if (size > MAX_BANNER_IMAGE_SIZE) {
        setImages(info.fileList.slice(0, -1));
        form?.setFieldsValue({ [images]: info?.fileList?.slice(0, -1) });
        message?.destroy();
        message?.error('Allowed maximum size is 200kb');
        return false;
      }
    }
  }
  form?.setFieldsValue({ [images]: info?.fileList });
  setImages(info?.fileList);
  return true;
};
export const validateImagesBrands = async (
  info,
  form,
  setImages,
  images,
  onlyFormatValidate = false,
) => {
  if (!info?.file?.url && !info?.file?.originFileObj) {
    const {
      file: { name: fileName = '', key = '', size },
    } = info;
    const ext = fileName?.substring(fileName?.lastIndexOf('.') + 1);
    const extKey = key?.substring(key?.lastIndexOf('.') + 1);

    if (
      !ALLOW_IMAGES_TYPES?.includes(ext?.toLowerCase() || extKey?.toLowerCase())
    ) {
      setImages(info?.fileList?.slice(0, -1));
      form?.setFieldsValue({ [images]: info?.fileList?.slice(0, -1) });
      message?.destroy();
      message?.error(`${info?.file?.name} file is not image file.`);
      return false;
    }

    if (!onlyFormatValidate) {
      const dimension = await getImageDimensions(info?.file);
      const { width, height } = dimension || {};
      if (width <= 700 || height <= 350) {
        setImages(info.fileList.slice(0, -1));
        form?.setFieldsValue({ [images]: info?.fileList?.slice(0, -1) });
        message?.destroy();
        message?.error(
          'The image is too small, the minimum dimensions are 750x400 pixels',
        );
        return false;
      }

      if (size > MAX_BRANDS_IMAGE_SIZE) {
        setImages(info.fileList.slice(0, -1));
        form?.setFieldsValue({ [images]: info?.fileList?.slice(0, -1) });
        message?.destroy();
        message?.error('Allowed maximum size is 200kb');
        return false;
      }
    }
  }
  form?.setFieldsValue({ [images]: info?.fileList });
  setImages(info?.fileList);
  return true;
};
export const validateImage = async (
  info,
  form,
  setImages,
  images,
  onlyFormatValidate = false,
) => {
  if (!info?.file?.url && !info?.file?.originFileObj) {
    const {
      file: { name: fileName = '', key = '', size },
    } = info;
    const ext = fileName?.substring(fileName?.lastIndexOf('.') + 1);
    const extKey = key?.substring(key?.lastIndexOf('.') + 1);

    if (
      !ALLOW_IMAGE_TYPES?.includes(ext?.toLowerCase() || extKey?.toLowerCase())
    ) {
      setImages(info?.fileList?.slice(0, -1));
      form?.setFieldsValue({ [images]: info?.fileList?.slice(0, -1) });
      message?.destroy();
      message?.error(`${info?.file?.name} file is not image file.`);
      return false;
    }

    if (!onlyFormatValidate) {
      const dimension = await getImageDimensions(info?.file);
      const { width, height } = dimension || {};
      // const aspectRatio = width / height;

      if (width <= 200 || height <= 200) {
        setImages(info.fileList.slice(0, -1));
        form?.setFieldsValue({ [images]: info?.fileList?.slice(0, -1) });
        message?.destroy();
        message?.error(
          'The image is too small, the minimum dimensions are 200x200 pixels',
        );
        return false;
      }
      if (size > MAX_IMAGE_SIZE) {
        setImages(info.fileList.slice(0, -1));
        form?.setFieldsValue({ [images]: info?.fileList?.slice(0, -1) });
        message?.destroy();
        message?.error('Allowed maximum size is 15Mb');
        return false;
      }
    }
  }
  form?.setFieldsValue({ [images]: info?.fileList });
  setImages(info?.fileList);
  return true;
};
export const validateStoryVideo = async (
  info,
  form,
  setToyVideo,
  video,
  onlyFormatValidate = false,
) => {
  if (!info?.file?.url && !info?.file?.originFileObj) {
    const {
      file: { name: fileName = '', key = '', size },
    } = info;
    const ext = fileName?.substring(fileName?.lastIndexOf('.') + 1);
    const extKey = key?.substring(key?.lastIndexOf('.') + 1);

    if (
      !ALLOW_VIDEO_TYPES?.includes(ext?.toLowerCase() || extKey?.toLowerCase())
    ) {
      setToyVideo(info?.fileList?.slice(0, -1));
      form?.setFieldsValue({ [video]: info?.fileList?.slice(0, -1) });
      message?.destroy();
      message?.error(
        `Only MP4 video allowed. ${info?.file?.name} file is not accepted video file.`,
      );
      return false;
    }

    if (!onlyFormatValidate) {
      if (size > MAX_STORY_VIDEO_SIZE) {
        setToyVideo(info.fileList.slice(0, -1));
        form?.setFieldsValue({ [video]: info?.fileList?.slice(0, -1) });
        message?.destroy();
        message?.error('Allowed maximum size is 30Mb');
        return false;
      }
    }
  }
  form?.setFieldsValue({ [video]: info?.fileList });
  setToyVideo(info?.fileList);
  return true;
};
export const validateVideo = async (
  info,
  form,
  setToyVideo,
  video,
  onlyFormatValidate = false,
) => {
  if (!info?.file?.url && !info?.file?.originFileObj) {
    const {
      file: { name: fileName = '', key = '', size },
    } = info;
    const ext = fileName?.substring(fileName?.lastIndexOf('.') + 1);
    const extKey = key?.substring(key?.lastIndexOf('.') + 1);

    if (
      !ALLOW_VIDEO_TYPES?.includes(ext?.toLowerCase() || extKey?.toLowerCase())
    ) {
      setToyVideo(info?.fileList?.slice(0, -1));
      form?.setFieldsValue({ [video]: info?.fileList?.slice(0, -1) });
      message?.destroy();
      message?.error(
        `Only MP4 video allowed. ${info?.file?.name} file is not accepted video file.`,
      );
      return false;
    }

    if (!onlyFormatValidate) {
      if (size > MAX_VIDEO_SIZE) {
        setToyVideo(info.fileList.slice(0, -1));
        form?.setFieldsValue({ [video]: info?.fileList?.slice(0, -1) });
        message?.destroy();
        message?.error('Allowed maximum size is 30Mb');
        return false;
      }
    }
  }
  form?.setFieldsValue({ [video]: info?.fileList });
  setToyVideo(info?.fileList);
  return true;
};

export const billableAmount = (price, memberShipFees, refundableDeposit) => {
  const totalAmount =
    parseInt(price || 0, 10) +
    (memberShipFees !== '-' ? parseInt(memberShipFees || 0, 10) : 0) +
    parseInt(refundableDeposit || 0, 10);
  return totalAmount;
};

export const hasNonEmptyPermission = (permissionKeys, permissions) => {
  if (typeof permissionKeys === 'string') {
    return (
      permissions?.[permissionKeys] !== undefined &&
      permissions?.[permissionKeys]?.length !== 0
    );
  }
  if (Array?.isArray(permissionKeys)) {
    return permissionKeys?.some(
      (permission) =>
        permissions?.[permission] !== undefined &&
        permissions?.[permission]?.length !== 0,
    );
  }
  return false;
};

export const hasMatchingTabKey = (tabKeys, permissions) =>
  tabKeys?.some((tab) => hasNonEmptyPermission(tab?.tabKey, permissions));

export const getRedirectItem = (permissions, array = [], isAllAccess = false) =>
  array
    ?.map((item) => {
      const matchingKey = item?.tabKeys?.find(
        (tab) =>
          Object?.prototype?.hasOwnProperty?.call(permissions, tab?.tabKey) &&
          permissions?.[tab?.tabKey]?.length > 0,
      );
      const matchingPermissionKey = hasNonEmptyPermission(
        [item?.permissionKey],
        permissions,
      );

      return matchingKey || matchingPermissionKey || isAllAccess
        ? item?.route
        : null;
    })
    ?.filter(Boolean);

export const getRedirectSubmenuItem = (permissions, array = []) =>
  array
    ?.map((item) =>
      hasNonEmptyPermission(item?.tabKey, permissions)
        ? item?.route
        : undefined,
    )
    ?.filter((item) => item);

export const filterTabsByPermissions = (tabList, permissions) => {
  const filteredTabs = tabList?.filter(
    (tab) =>
      Object?.prototype?.hasOwnProperty?.call(
        permissions,
        tab?.permissionKey,
      ) && permissions?.[tab?.permissionKey]?.length > 0,
  );
  return filteredTabs;
};

export const hasPermission = (permissionKey, permissionType, permissions) => {
  if (Array?.isArray(permissionType)) {
    return permissionType?.some((type) =>
      permissions?.[permissionKey]?.includes(type),
    );
  }
  return permissions?.[permissionKey]?.includes(permissionType) ?? false;
};

export const handleDownload = (fileUrl) => {
  // eslint-disable-next-line no-undef
  window.open(fileUrl, '_blank');
  // const fileExtension = fileUrl.match(/\.(\w+)\?/)?.[1];
  // if (fileUrl) {
  //   fetch(fileUrl)
  //     ?.then((response) => response?.blob())
  //     ?.then((blob) => {
  //       // eslint-disable-next-line no-undef
  //       const url = window?.URL?.createObjectURL(blob);
  //       // eslint-disable-next-line no-undef
  //       const link = document?.createElement('a');
  //       link.href = url;
  //       const filename = `${Date.now()}.${fileExtension}`;
  //       link.download = filename;
  //       // eslint-disable-next-line no-undef
  //       document?.body?.appendChild(link);
  //       link?.click();
  //       // eslint-disable-next-line no-undef
  //       document?.body?.removeChild(link);
  //     })
  //     ?.catch((error) => error);
  // }
};

export const handleCopy = (event) => {
  // eslint-disable-next-line no-undef
  window?.navigator?.clipboard?.writeText(event);
  message?.destroy();
  message?.success('Text copied to clipboard');
};

export const hasNonEmptyValueObj = (obj) =>
  Object?.values(obj || {})?.some((value) => {
    if (value !== null && value !== undefined) {
      if (Array?.isArray(value) && value?.length > 0) {
        return true;
      }
      if (!Array?.isArray(value)) {
        return true;
      }
    }
    return false;
  });

export const handleInput = (e, isAllowUnderscore = false) => {
  let inputValue = e.target.value.toUpperCase();
  inputValue = inputValue.replace(
    isAllowUnderscore
      ? REGEX?.ALLOW_STRING_NUM_UNDERSCORE
      : REGEX?.NOT_ALLOW_SPECIAL_CHAR,
    '',
  );
  e.target.value = inputValue;
};

export const getVideoDetails = (file) =>
  new Promise((resolve, reject) => {
    // eslint-disable-next-line no-undef
    const reader = new FileReader();

    reader.onload = (e) => {
      // eslint-disable-next-line no-undef
      const video = document?.createElement('video');

      if (video) {
        video.onloadedmetadata = async () => {
          const width = video?.videoWidth;
          const height = video?.videoHeight;
          const { duration } = video;
          const { type } = file;
          const sizeInMB = file?.size / (1024 * 1024);
          resolve({
            width,
            height,
            duration,
            type,
            sizeInMB,
          });
        };

        video.onerror = (error) => {
          reject(error);
        };

        video.src = e?.target?.result;
      }
    };

    reader.readAsDataURL(file);
  });

export const formatNameToSlug = (name) =>
  name
    ?.trim()
    ?.replace(/[^a-zA-Z0-9- ]+/g, '')
    ?.replace(/ /g, '-')
    ?.toLowerCase();

export const getPlayPlanLabel = (plan) => {
  const planString = plan?.slug?.split('-')?.map((item) => capitalize(item));
  return planString ? planString?.join(' ') : PLAN_NAMES?.[plan?.name]?.key;
};

export const getTimePercentage = (record) => {
  const orderTime = new Date(record?.currentStatusTime)?.getTime();
  const deadline = new Date(record?.nextStatusTime)?.getTime();
  const totalBreachTime = deadline - orderTime;
  const currentTime = Date.now();
  const remaining = deadline - currentTime;
  const percentage = (remaining / totalBreachTime) * 100;
  return Math.max(0, percentage);
};

export const getRowClassName = (
  record,
  thresholdData,
  playNotificationSound,
  criticalOrdersRef,
) => {
  if (
    record?.isExpressDelivery &&
    record?.nextStatusTime &&
    record?.currentStatusTime &&
    record?.status !== ORDER_STATUS.DELIVERED.value &&
    record?.status !== ORDER_STATUS.CANCELLED.value
  ) {
    const percentage = getTimePercentage(record);
    const orderId = record?.id;
    const status = record?.status;
    const key = `${orderId}_${status}`;

    if (percentage === 0) {
      if (criticalOrdersRef?.current?.has(key)) {
        criticalOrdersRef?.current?.delete(key);
        // eslint-disable-next-line no-undef
        localStorage.setItem(
          CRITICAL_ORDERS_KEY,
          JSON.stringify(Array.from(criticalOrdersRef?.current?.keys())),
        );
      }
      return 'order-critical';
    }

    if (percentage < thresholdData?.criticalThreshold) {
      if (!criticalOrdersRef?.current?.has(key)) {
        criticalOrdersRef?.current?.add(key);
        // eslint-disable-next-line no-undef
        localStorage.setItem(
          CRITICAL_ORDERS_KEY,
          JSON.stringify(Array.from(criticalOrdersRef?.current?.keys())),
        );

        playNotificationSound(record);
      }
      return 'order-critical';
    }

    if (
      percentage >= thresholdData?.criticalThreshold &&
      percentage <= thresholdData?.warningThreshold
    ) {
      if (criticalOrdersRef?.current?.has(key)) {
        criticalOrdersRef?.current?.delete(key);

        // eslint-disable-next-line no-undef
        localStorage.setItem(
          CRITICAL_ORDERS_KEY,
          JSON.stringify(Array.from(criticalOrdersRef?.current?.keys())),
        );
      }
      return 'order-warning';
    }

    if (criticalOrdersRef?.current?.has(key)) {
      criticalOrdersRef?.current?.delete(key);
      // eslint-disable-next-line no-undef
      localStorage.setItem(
        CRITICAL_ORDERS_KEY,
        JSON.stringify(Array.from(criticalOrdersRef?.current?.keys())),
      );
    }
    return 'order-on-track';
  }

  return '';
};

export const renderer = ({ hours, minutes, seconds }) => (
  // eslint-disable-next-line react/react-in-jsx-scope
  <span>
    {hours}h {minutes}m {seconds}s
  </span>
);
